import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface IndustrialFocusTarget {
  readonly id: number;
  readonly lat: number;
  readonly lon: number;
  /** Monotonic token so re-focusing the same object re-triggers the map. */
  readonly token: number;
}

interface IndustrialFocusValue {
  readonly pending: IndustrialFocusTarget | null;
  /** Request the map to fly to and open the card for a reference-book object. */
  readonly focus: (id: number, lat: number, lon: number) => void;
  readonly clear: () => void;
}

const IndustrialFocusContext = createContext<IndustrialFocusValue | undefined>(
  undefined,
);

export function IndustrialFocusProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<IndustrialFocusTarget | null>(null);

  const focus = useCallback((id: number, lat: number, lon: number) => {
    setPending((prev) => ({ id, lat, lon, token: (prev?.token ?? 0) + 1 }));
  }, []);

  const clear = useCallback(() => setPending(null), []);

  const value = useMemo<IndustrialFocusValue>(
    () => ({ pending, focus, clear }),
    [pending, focus, clear],
  );

  return (
    <IndustrialFocusContext.Provider value={value}>
      {children}
    </IndustrialFocusContext.Provider>
  );
}

export function useIndustrialFocus(): IndustrialFocusValue {
  const ctx = useContext(IndustrialFocusContext);
  if (!ctx) {
    throw new Error(
      'useIndustrialFocus must be used within IndustrialFocusProvider',
    );
  }
  return ctx;
}
